import React, { useEffect, useMemo, useRef } from 'react';
import { Polyline, Marker, Tooltip } from 'react-leaflet';
import './trackingStyles.css';
import { createLeafletPinIcon } from '../map/leafletPinIcon';
import { trustedVoyageStops, voyageHasDelay } from './ShipMarker';
import { resolvePortLatLng, samePortName, haversineKm, rowLatLng } from './portCoords';
import { extractSeaLegs, splitAroundPin, splitAtDateLine, wrapPoint } from '../../../utils/routeGeojson';


/**
 * Route longitudes are unwrapped upstream (they can run past +-180) so no
 * polyline carries a 360 degree step. Here they come back into the base world
 * copy, cut at the date line: a crossing draws as two pieces that stop at the
 * edges instead of one line sweeping back across the map when zoomed out.
 *
 * Deliberately not repeated into the neighbouring copies — Leaflet draws
 * markers only once, so a repeated route would show with no ship on it.
 */
const RoutePolyline = ({ positions, ...props }) => (
  <>
    {splitAtDateLine(positions).map((piece, i) => (
      <Polyline key={`copy-${i}`} positions={piece} {...props} />
    ))}
  </>
);


/** Teardrop at a port. React-Leaflet ignores icon prop updates — must setIcon. */
function DelayedPortPin({ position, label, delayed, sizePx = 22, showLabel = true }) {
  const fill = delayed ? '#dc2626' : '#3b82f6';
  const icon = useMemo(
    () => createLeafletPinIcon({
      fillColor: fill,
      sizePx,
      className: `route-pin-icon ${delayed ? 'pod-pin--delayed' : 'pod-pin--ok'}`,
    }),
    [delayed, sizePx, fill],
  );
  const ref = useRef(null);
  useEffect(() => {
    const layer = ref.current;
    if (layer && icon && typeof layer.setIcon === 'function') layer.setIcon(icon);
  }, [icon]);
  if (!position) return null;
  return (
    <Marker
      ref={ref}
      key={`${label}-${delayed ? 'RED' : 'BLUE'}-${position[0]}-${position[1]}`}
      position={position}
      icon={icon}
      title=""
      alt=""
      zIndexOffset={delayed ? 500 : 0}
    >
      {showLabel ? (
        <Tooltip permanent direction="bottom" className="port-label">{label}</Tooltip>
      ) : null}
    </Marker>
  );
}



/**
 * Current-vessel route only. voyage_stops pins/extra legs come from the API
 * (already IMO-merged). Never invent east NAE∪SAE siblings.
 */
const RouteLayer = ({
  shipment,
  routeGeojson,
  departurePort,
  arrivalPort,
  voyageStops,
  hasShipsgoCurrent,
  vessel,
  currentVessel,
  delayDays,
  rowIsDelayed,
  // Full five-status palette for the Global Produce Map, where the route has
  // to match the marker's colour (ahead = green, at port = orange, discharged
  // = grey). Omitted by Ship Tracking, which keeps the original
  // delayed/on-time pair below.
  statusColor,
  preview = false,
}) => {

  const stops = useMemo(
    () => trustedVoyageStops({
      voyage_stops: voyageStops,
      has_shipsgo_current: hasShipsgoCurrent,
      arrival_port: arrivalPort,
      vessel,
      current_vessel: currentVessel,
    }),
    [voyageStops, hasShipsgoCurrent, arrivalPort, vessel, currentVessel],
  );

  const stopPins = useMemo(() => {
    return stops.map((stop, idx) => {
      const port = stop?.arrival_port || '';
      const pos = resolvePortLatLng(port, stop);
      if (!pos) return null;
      const stopDelayed = voyageHasDelay({ delay_days: stop?.delay_days });
      return { port, pos, idx, isCurrent: samePortName(port, arrivalPort), delayed: stopDelayed };
    }).filter(Boolean);
  }, [stops, arrivalPort]);

  const rowDelayed = (
    rowIsDelayed === true
    || voyageHasDelay(shipment)
    || Number(shipment?.delay_days ?? delayDays) >= 1
  );


  const seaLegs = useMemo(() => extractSeaLegs(routeGeojson), [routeGeojson]);
  // Leg coords are unwrapped for the polylines; pins are single markers and
  // never repeat, so they go back into one world copy.
  const polCoord = wrapPoint(seaLegs[0]?.coords?.[0]);
  const podCoord = wrapPoint((() => {
    const last = seaLegs[seaLegs.length - 1];
    return last?.coords?.[last.coords.length - 1];
  })());

  if (!routeGeojson && stopPins.length === 0) return null;
  if (seaLegs.length === 0 && !polCoord && !podCoord && stopPins.length === 0) {
    return null;
  }

  const podCovered = stopPins.some((s) => s.isCurrent);
  const routeColor = statusColor || (rowDelayed ? '#dc2626' : '#22c55e');
  const ais = rowLatLng(shipment);
  const labelOk = (pos) => !ais || !pos || haversineKm(pos, ais) >= 8;
  const pod = resolvePortLatLng(arrivalPort) || stopPins.find((s) => s.isCurrent)?.pos;
  const aisSplit = splitAroundPin(seaLegs, ais, pod);
  const hasSplit = aisSplit.traveled.length >= 2 || aisSplit.remaining.length >= 2;

  return (
    <>
      {hasSplit ? (
        <>
          {aisSplit.traveled.length >= 2 && (
            <RoutePolyline
              positions={aisSplit.traveled}
              pathOptions={{ color: routeColor, weight: 4, opacity: 0.85 }}
              className={`route-past${rowDelayed ? " route-delayed" : ""}`}
            />
          )}
          {aisSplit.remaining.length >= 2 && (
            <RoutePolyline
              positions={aisSplit.remaining}
              className={`route-future${rowDelayed ? ' route-delayed' : ''}`}
              pathOptions={{ color: routeColor, weight: 4, opacity: 0.6, lineCap: 'round', dashArray: '4 10' }}
            />
          )}
        </>
      ) : seaLegs.map((leg, i) => {
        const past = leg.status === 'PAST';
        return (
          <RoutePolyline
            key={`leg-${i}-${leg.status || 'sea'}`}
            positions={leg.coords}
            className={`${past ? 'route-past' : 'route-future'}${rowDelayed ? ' route-delayed' : ''}`}
            pathOptions={{ color: routeColor, weight: 4, opacity: past ? 0.85 : 0.6, lineCap: 'round', dashArray: past ? undefined : '4 10' }}
          />
        );
      })}

      {!preview && polCoord && (
        <Marker
          position={polCoord}
          icon={createLeafletPinIcon({ fillColor: '#06b6d4', sizePx: 22, className: 'route-pin-icon' })}
          title=""
          alt=""
        >
          {labelOk(polCoord) ? (
            <Tooltip permanent direction="bottom" className="port-label">
              POL {departurePort}
            </Tooltip>
          ) : null}
        </Marker>
      )}

      {!preview && stopPins.map((s) => (
        <DelayedPortPin
          key={`stop-${s.port}-${s.idx}-${(s.delayed || rowDelayed) ? 'RED' : 'BLUE'}`}
          position={s.pos}
          label={s.isCurrent ? `POD ${s.port}` : s.port}
          delayed={Boolean(s.delayed || rowDelayed)}
          sizePx={s.isCurrent ? 22 : 18}
          showLabel={labelOk(s.pos)}
        />
      ))}

      {!preview && podCoord && !podCovered && (
        <DelayedPortPin
          key={`pod-${arrivalPort}-${rowDelayed ? 'RED' : 'BLUE'}`}
          position={podCoord}
          label={`POD ${arrivalPort}`}
          delayed={Boolean(rowDelayed)}
          sizePx={22}
          showLabel={labelOk(podCoord)}
        />
      )}
    </>
  );
};

export default RouteLayer;
